import { Link } from "react-router-dom";
import { 
  FileImage, Images, Scissors, Combine, Clock, Shield, Zap,
  Minimize2, RotateCw, Trash, ArrowUpDown, ScanLine, FileText,
  PenTool, Edit3, Droplets, Lock, Unlock
} from "lucide-react";
import SEOHead from "@/components/SEOHead";

const tools = [
  // Convert
  {
    id: "pdf-to-image",
    title: "PDF to Image",
    description: "Convert PDF pages to PNG or JPEG images",
    icon: FileImage,
    path: "/pdf-to-image",
    category: "convert",
  },
  {
    id: "image-to-pdf",
    title: "Image to PDF",
    description: "Combine images into a single PDF",
    icon: Images,
    path: "/image-to-pdf",
    category: "convert",
  },
  {
    id: "pdf-ocr",
    title: "PDF OCR",
    description: "Extract text from scanned PDFs",
    icon: ScanLine,
    path: "/pdf-ocr",
    category: "convert",
  },
  // Organize
  {
    id: "pdf-merge",
    title: "Merge PDF",
    description: "Combine multiple PDFs into one",
    icon: Combine,
    path: "/pdf-merge",
    category: "organize",
  },
  {
    id: "pdf-split",
    title: "Split PDF",
    description: "Extract pages or split into files",
    icon: Scissors,
    path: "/pdf-split",
    category: "organize",
  },
  {
    id: "pdf-organize",
    title: "Organize PDF",
    description: "Rearrange pages in any order",
    icon: ArrowUpDown,
    path: "/pdf-organize",
    category: "organize",
  },
  // Edit
  {
    id: "pdf-edit",
    title: "Edit PDF",
    description: "Add or change text in your PDF",
    icon: Edit3,
    path: "/pdf-edit",
    category: "edit",
  },
  {
    id: "pdf-sign",
    title: "Sign PDF",
    description: "Add your signature to documents",
    icon: PenTool,
    path: "/pdf-sign",
    category: "edit",
  },
  {
    id: "pdf-rotate",
    title: "Rotate PDF",
    description: "Rotate pages to correct orientation",
    icon: RotateCw,
    path: "/pdf-rotate",
    category: "edit",
  },
  {
    id: "pdf-delete-pages",
    title: "Delete Pages",
    description: "Remove unwanted pages",
    icon: Trash,
    path: "/pdf-delete-pages",
    category: "edit",
  },
  {
    id: "pdf-watermark",
    title: "Watermark PDF",
    description: "Add text or image watermarks",
    icon: Droplets,
    path: "/pdf-watermark",
    category: "edit",
  },
  // Security
  {
    id: "pdf-protect",
    title: "Protect PDF",
    description: "Add password protection",
    icon: Lock,
    path: "/pdf-protect",
    category: "security",
  },
  {
    id: "pdf-unlock",
    title: "Unlock PDF",
    description: "Remove password protection",
    icon: Unlock,
    path: "/pdf-unlock",
    category: "security",
  },
  // Optimize
  {
    id: "pdf-compress",
    title: "Compress PDF",
    description: "Reduce file size",
    icon: Minimize2,
    path: "/pdf-compress",
    category: "optimize",
  },
  {
    id: "pdf-scanned",
    title: "Scanned Effect",
    description: "Make PDF look like scanned",
    icon: FileText,
    path: "/pdf-scanned",
    category: "optimize",
  },
];

const features = [
  {
    icon: Clock,
    title: "Auto-Delete",
    description: "Files automatically deleted after 30 minutes",
  },
  {
    icon: Shield,
    title: "Privacy First",
    description: "No account required, no data stored",
  },
  {
    icon: Zap,
    title: "Fast Processing",
    description: "Instant conversion with original quality",
  },
];

const HomePage = () => {
  return (
    <>
      <SEOHead
        title="SwissFlow PDF - Free Online PDF Tools | No Signup"
        description="Free online PDF tools - Convert, merge, split, compress, edit, sign, protect PDFs. No signup required. 15+ tools. Files auto-delete in 30 minutes."
        canonicalPath="/"
        keywords="free PDF tools, PDF converter, merge PDF, split PDF, compress PDF, edit PDF online, PDF editor free, online PDF tools"
      />
      
      <div className="min-h-[calc(100vh-8rem)]">
      {/* Hero Section */}
      <section className="py-16 md:py-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-mono tracking-wide uppercase text-muted-foreground mb-4 animate-fade-in-up">
              15+ Free PDF Tools • No Signup Required
            </p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-none mb-6 animate-fade-in-up animate-delay-100">
              All PDF Tools
              <br />
              <span className="text-[#FF3B30]">In One Place</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl animate-fade-in-up animate-delay-200">
              Convert, compress, merge, split, edit, sign, protect, OCR and more. 
              Files auto-delete in 30 minutes for your privacy.
            </p>
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="pb-16 md:pb-24" data-testid="tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Convert Section */}
          <div className="mb-12">
            <h2 className="text-sm font-mono tracking-wide uppercase text-muted-foreground mb-6">
              Convert
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tools.filter(t => t.category === "convert").map((tool, index) => (
                <ToolCard key={tool.id} tool={tool} index={index} accent />
              ))}
            </div>
          </div>

          {/* Organize Section */}
          <div className="mb-12">
            <h2 className="text-sm font-mono tracking-wide uppercase text-muted-foreground mb-6">
              Organize
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tools.filter(t => t.category === "organize").map((tool, index) => (
                <ToolCard key={tool.id} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* Edit Section */}
          <div className="mb-12">
            <h2 className="text-sm font-mono tracking-wide uppercase text-muted-foreground mb-6">
              Edit & Sign
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {tools.filter(t => t.category === "edit").map((tool, index) => (
                <ToolCard key={tool.id} tool={tool} index={index} compact />
              ))}
            </div>
          </div>

          {/* Security Section */}
          <div className="mb-12">
            <h2 className="text-sm font-mono tracking-wide uppercase text-muted-foreground mb-6">
              Security
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tools.filter(t => t.category === "security").map((tool, index) => (
                <ToolCard key={tool.id} tool={tool} index={index} />
              ))}
            </div>
          </div>

          {/* Optimize Section */}
          <div className="mb-12">
            <h2 className="text-sm font-mono tracking-wide uppercase text-muted-foreground mb-6">
              Optimize & Effects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tools.filter(t => t.category === "optimize").map((tool, index) => (
                <ToolCard key={tool.id} tool={tool} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 border-t border-border" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <div 
                key={feature.title} 
                className="flex flex-col animate-fade-in-up"
                style={{ animationDelay: `${(index + 1) * 100}ms` }}
                data-testid={`feature-${feature.title.toLowerCase().replace(' ', '-')}`}
              >
                <div className="w-10 h-10 bg-secondary flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-[#FF3B30]" />
                </div>
                <h4 className="text-lg font-semibold tracking-tight mb-2">
                  {feature.title}
                </h4>
                <p className="text-muted-foreground text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
            Start Working with PDFs
          </h2>
          <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">
            No signup, no limits, no hassle. Just drag, drop, and download.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/pdf-edit"
              className="bg-[#FF3B30] text-white px-8 py-4 font-medium btn-hover-lift"
              data-testid="cta-edit"
            >
              Edit PDF
            </Link>
            <Link
              to="/pdf-protect"
              className="bg-white text-black px-8 py-4 font-medium btn-hover-lift"
              data-testid="cta-protect"
            >
              Protect PDF
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

const ToolCard = ({ tool, index, accent = false, compact = false }) => {
  return (
    <Link
      to={tool.path}
      className={`tool-card group animate-fade-in-up ${compact ? "p-5" : "p-6"}`}
      style={{ animationDelay: `${(index + 1) * 100}ms` }}
      data-testid={`tool-card-${tool.id}`}
    >
      <div className="flex flex-col h-full">
        <div className={`${compact ? "w-10 h-10 mb-4" : "w-12 h-12 mb-6"} flex items-center justify-center ${
          accent ? "bg-[#FF3B30]" : "bg-black"
        }`}>
          <tool.icon className={`${compact ? "w-5 h-5" : "w-6 h-6"} text-white`} />
        </div>
        <h3 className={`${compact ? "text-lg" : "text-xl md:text-2xl"} font-semibold tracking-tight mb-2 group-hover:text-[#FF3B30] transition-colors`}>
          {tool.title}
        </h3>
        <p className={`text-muted-foreground ${compact ? "text-sm" : ""} leading-relaxed flex-1`}>
          {tool.description}
        </p>
        <div className="mt-4 pt-4 border-t border-border">
          <span className="text-xs font-mono tracking-wide uppercase text-[#FF3B30] group-hover:underline">
            Open Tool →
          </span>
        </div>
      </div>
    </Link>
  );
};

export default HomePage;
